import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { BehaviorSubject, Observable, Subscription, firstValueFrom } from 'rxjs';
import { SeFeApiService } from 'se-fe-api';
import { SeFePaginationChange, SeFePaginationOptions } from 'se-fe-pagination';
import { SeFeTableComponent, SeFeTableDataSource } from 'se-fe-table';
import { SeFeToolbarComponent } from 'se-fe-toolbar';
import { ItemVariation } from '../../sale-items/models/item-variation.model';
import { ItemVariationService } from '../../sale-items/services/item-variation.service';

@Component({
  selector: 'app-sale-item-modal',
  standalone: false,
  templateUrl: './sale-item-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./sale-item-modal.component.scss'],
})
export class SaleItemModalComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('eligibleItemDialog') public eligibleItemDialog: ElementRef<HTMLDialogElement>;
  @ViewChild(SeFeTableComponent) public displayTable!: SeFeTableComponent<ElementRef>;
  @ViewChild('displayTable') set displayTableComponent(displayTable: SeFeTableComponent<ElementRef>) {
    if (!displayTable) {
      return;
    }
    this.displayTable = displayTable;
  }
  @ViewChild(SeFeTableComponent) public selectTable!: SeFeTableComponent<ElementRef>;
  @ViewChild('selectTable') set selectTableComponent(selectTable: SeFeTableComponent<ElementRef>) {
    if (!selectTable) {
      return;
    }
    this.selectTable = selectTable;
    this.selectTable.dataSource = this.selectTableDataSource;
  }
  @ViewChild(SeFeToolbarComponent) public toolbar: SeFeToolbarComponent;

  @Input() modalHeader: string;
  @Input() organizationId: string;
  @Input() selectedItemIds: number[];

  @Output() public selectionSaved = new EventEmitter<any>();

  private formSubscription: Subscription;
  private initialSelectedItems: ItemVariation[] = [];
  public amountSelected: number;
  public displayTableColumns = new BehaviorSubject<string[]>(['name', 'sku', 'price', 'remove']);
  displayTableDataSource$ = new BehaviorSubject<SeFeTableDataSource>(undefined);
  public isLoaded = false;
  public itemVariationList: ItemVariation[] = [];
  public itemVariations$: Observable<ItemVariation[]>;
  public paginationOptions: SeFePaginationOptions;
  public searchTerm = '';
  public selectAllOptions = [{ label: 'Select All', value: true, hiddenLabel: true }];
  public selectedItems: ItemVariation[] = [];
  public selectedItems$: Observable<number[]>;
  public selectedItemsCount$: Observable<number>;
  public selectedItemsLoaded = true;
  public selectForm: FormGroup;
  public selectTableColumns = new BehaviorSubject<string[]>(['checkbox', 'name', 'sku', 'price']);
  public selectTableDataSource: SeFeTableDataSource;
  public query$ = new BehaviorSubject('');
  public validationError: string = '';
  private hasInitialized = false;

  constructor(
    private apiService: SeFeApiService,
    private itemVariationService: ItemVariationService,
    private fb: FormBuilder
  ) {}

  public async ngOnInit(): Promise<void> {
    this.amountSelected = 0;
    this.paginationOptions = { label: 'sale items', name: 'itemVariation', currentPage: 1, limit: 25, total: 0 };
    this.selectTableDataSource = new SeFeTableDataSource([]);

    this.createForm();

    await this.loadItems();
    await this.syncSelectedItemsFromInput();
    this.updateForm();
    this.hasInitialized = true;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!this.hasInitialized || !changes.selectedItemIds) {
      return;
    }

    void this.syncSelectedItemsFromInput();
  }

  public createForm(): void {
    this.selectForm = this.fb.group({
      selectAll: [false],
    });

    this.selectForm.controls.selectAll.valueChanges.subscribe((isSelected: boolean) => {
      this.toggleAllSelections(isSelected);
    });

    this.subscribeToSelectFormChanges();
  }

  queryChange(query: string): void {
    this.query$.next(query);
  }

  private toggleSelection(formValue: { [id: string]: boolean }) {
    for (const id in formValue) {
      if (!id) {
        continue;
      }
      const itemIndex = this.selectedItemIndex(parseInt(id, 10));
      if (!!formValue[id] && itemIndex === -1) {
        const itemModel = this.itemVariationList.find((item) => item.id.toString() === id);
        if (itemModel) {
          this.selectedItems.push(itemModel);
        }
      }
      if (!formValue[id] && itemIndex !== -1) {
        this.selectedItems.splice(itemIndex, 1);
      }
    }
  }

  toggleAllSelections(isSelected: boolean): void {
    this.itemVariationList.forEach((item) => {
      this.selectForm.controls[item.id.toString()]?.setValue(isSelected, { emitEvent: false });
    });

    if (isSelected) {
      this.selectedItems = [...this.itemVariationList];
    } else {
      this.selectedItems = [];
    }

    this.updateDisplayTable();
  }

  public removeItem(itemId: number): void {
    const itemIndex = this.selectedItemIndex(itemId);
    if (itemIndex !== -1) {
      this.selectedItems.splice(itemIndex, 1);
      this.updateDisplayTable();
      this.updateForm();
      this.selectionSaved.emit(this.currentlySelected());
    }
  }

  public async saveSelection(): Promise<void> {
    if (this.selectedItems.length === 0) {
      this.validationError = 'Please select at least one sale item before saving.';
      return;
    }
    this.validationError = '';
    this.updateDisplayTable();
    this.selectionSaved.emit(this.currentlySelected());
    this.eligibleItemDialog.nativeElement.close();
  }

  public async resetSelection(): Promise<void> {
    this.selectedItems = [...this.initialSelectedItems]; // Restore initial state
    this.updateDisplayTable();
    this.updateForm();
    this.eligibleItemDialog.nativeElement.close();
  }

  public async handlePagination(paginationData: SeFePaginationChange): Promise<void> {
    this.paginationOptions.limit = paginationData.limit;
    this.paginationOptions.currentPage = paginationData.currentPage;
    await this.loadItems();
  }

  public async search(searchTerm: string): Promise<void> {
    this.searchTerm = searchTerm;
    this.paginationOptions.currentPage = 1;
    await this.loadItems();
  }

  public openEligibleItemDialog(): void {
    this.eligibleItemDialog.nativeElement.showModal();
  }

  public handleDialogCancel(event: Event): void {
    event.preventDefault();
    void this.resetSelection();
  }

  public handleDialogClick(event: MouseEvent): void {
    if (event.target === this.eligibleItemDialog.nativeElement) {
      void this.resetSelection();
    }
  }

  public async handleModalClose(): Promise<void> {
    await this.resetModal();
  }

  public async resetModal(): Promise<void> {
    this.validationError = '';
    this.paginationOptions.currentPage = 1;
    this.paginationOptions.limit = 25;
    this.searchTerm = '';
    this.toolbar?.searchForm.setValue({ searchTerm: '' }, { emitEvent: false });
    await this.syncSelectedItemsFromInput();
    await this.loadItems();
  }

  public async loadItems(): Promise<void> {
    this.isLoaded = false;
    const params = {
      ...this.baseParams,
      ...this.paginationParams,
      ...this.searchParams,
      ...this.sortParams,
    };
    try {
      const results = await firstValueFrom(this.apiService.get<ItemVariation[]>('finance/item_variations', { params }));
      this.itemVariationList = results.result;
      this.selectTableDataSource.data = this.itemVariationList;
      this.setPaginationOptions(results.metadata.pagination);
      this.updateForm();
    } finally {
      // Must stay after updateForm(): the spinner is the only signal that the rows are ready, and a
      // checkbox toggled before the form group is rebuilt loses its selection.
      this.isLoaded = true;
    }
  }

  public async loadSelectedItems(selectedItemIds: number[]): Promise<void> {
    if (!selectedItemIds?.length) {
      this.selectedItems = [];
      this.updateDisplayTable();
      return;
    }

    const params = {
      id_list: selectedItemIds.join(','),
      unpaginated: true,
      ...this.baseParams,
    };
    const results = await firstValueFrom(this.apiService.get<ItemVariation[]>('finance/item_variations', { params }));

    this.selectedItems = results.result;
    this.updateDisplayTable();
  }

  updateDisplayTable(): void {
    this.sortSelected();
    this.displayTableDataSource$.next(new SeFeTableDataSource(this.selectedItems));
    this.selectedItemIds = this.currentlySelected();
    this.amountSelected = this.selectedItems.length;
  }

  updateForm(): void {
    this.selectForm = this.fb.group({
      selectAll: [false],
    });
    this.formSubscription?.unsubscribe();
    this.itemVariationList.forEach((item) => {
      const isChecked = this.currentlySelected().includes(item.id);
      this.selectForm.addControl(String(item.id), new FormControl(isChecked));
    });
    this.formSubscription = this.selectForm.valueChanges.subscribe((formValue) => this.toggleSelection(formValue));
    this.selectForm.controls.selectAll.valueChanges.subscribe((isSelected: boolean) => {
      this.toggleAllSelections(isSelected);
    });

    this.subscribeToSelectFormChanges();
  }

  private subscribeToSelectFormChanges(): void {
    this.selectForm.valueChanges.subscribe((value) => {
      if (Object.values(value).includes(false)) {
        this.selectForm.controls.selectAll.setValue(false, { emitEvent: false });
      }
    });
  }

  private selectedItemIndex(itemId: number) {
    return this.selectedItems.findIndex((item) => item.id === itemId);
  }

  currentlySelected() {
    return this.selectedItems.map((item) => item.id);
  }

  private setPaginationOptions(paginationData: { current_page: number; total: number }): void {
    this.paginationOptions.name = 'itemListPagination';
    this.paginationOptions.label = 'sale items';
    this.paginationOptions.currentPage = paginationData.current_page;
    this.paginationOptions.total = paginationData.total;
  }

  private sortSelected(): void {
    this.selectedItems.sort((a, b) => a.full_name.localeCompare(b.full_name));
  }

  private async syncSelectedItemsFromInput(): Promise<void> {
    await this.loadSelectedItems(this.selectedItemIds || []);
    this.initialSelectedItems = [...this.selectedItems];
  }

  // Build Query Params
  private get baseParams(): any {
    return {
      organization_id: this.organizationId,
      include_zero_rate: 1,
      exclude_bundled: true,
    };
  }

  private get paginationParams(): any {
    return {
      page: this.paginationOptions.currentPage || 1,
      per_page: this.paginationOptions.limit || 25,
    };
  }

  private get searchParams(): any {
    return {
      search_fields: 'full_name,sku',
      search_term: this.searchTerm,
    };
  }

  private get sortParams(): any {
    return {
      'order[full_name]': 'asc',
    };
  }

  public ngOnDestroy(): void {
    this.formSubscription?.unsubscribe();
    if (this.selectForm) {
      this.selectForm.reset();
    }
  }
}
